import { Container, GridContainer, Heading } from "@fluidattacks/design";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PluggableList } from "react-markdown/lib";
import rehypeRemoveImages from "rehype-remove-images";
import rehypeSanitize from "rehype-sanitize";
import { useTheme } from "styled-components";

import { Prompts } from "../prompts";
import { useChatStore } from "../use-chat-store";
import { TextWithGradient } from "components/text-with-gradient";
import { authContext } from "context/auth";

const Welcome = ({
  isExpanded,
}: {
  isExpanded: boolean;
}): JSX.Element | undefined => {
  const { userName } = useContext(authContext);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { messages } = useChatStore();
  const theme = useTheme();

  const alignItems = isExpanded ? "center" : "flex-start";
  const textAlign = isExpanded ? "center" : "left";
  useEffect((): void => {
    if (messages.length > 0) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [messages, setIsOpen]);

  const numberOfColumns = isExpanded ? 2 : 1;

  if (isOpen) {
    return (
      <Container
        alignItems={alignItems}
        display={"flex"}
        flexDirection={"column"}
        gap={1}
        justify={"center"}
        pb={1}
      >
        <div>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: textAlign,
              width: "100%",
            }}
          >
            <TextWithGradient
              $align={textAlign}
              $gradient={"90deg, #F32637 0%, #BF0B1A 100%"}
            >
              {t("chat.welcome.title", { userName })}
            </TextWithGradient>
            <Heading display={"inline"} size={"md"} textAlign={textAlign}>
              {"👋"}
            </Heading>
          </div>
          <Heading lineSpacing={2} size={"lg"} textAlign={textAlign}>
            {t("chat.welcome.subtitle")}
          </Heading>
        </div>
        <p
          style={{
            color: theme.palette.gray[800],
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {t("chat.welcome.quickGuide.title")}
        </p>
        <MDEditor.Markdown
          rehypePlugins={[rehypeRemoveImages, rehypeSanitize] as PluggableList}
          source={t("chat.welcome.quickGuide.description")}
          style={{
            backgroundColor: "transparent",
            borderRadius: "12px",
            color: theme.palette.gray[600],
            fontFamily: "Roboto",
            fontSize: "14px",
            width: "100%",
            wordBreak: "break-word",
          }}
        />
        <GridContainer
          gap={0.75}
          lg={numberOfColumns}
          md={numberOfColumns}
          sm={numberOfColumns}
          xl={numberOfColumns}
        >
          <Prompts />
        </GridContainer>
      </Container>
    );
  }

  return undefined;
};

export { Welcome };
